import * as echarts from "echarts";
import {
  getLitrosProducto,
  LoteMedido,
  ParametrosMedicion,
} from "../../../../modulos/aplicaciones/calculoAplicacion";
import { PALETA_CATEGORICA } from "../../monitoreo/sesion/dashboard/monitoreoAnalisisPaleta";

/** Un producto de la aplicación, con su dosis por recipiente. */
export type ProductoDosis = {
  nombre: string;
  dosisMlPorRecipiente: number;
};

/** Color de la serie de un producto, por su posición. Lo exporta para que la
 *  leyenda que arma el campo de lotes use **el mismo** que las barras: dos
 *  paletas en paralelo se desincronizan en cuanto una de las dos cambie. */
export const fx_colorProducto = (indice: number): string =>
  PALETA_CATEGORICA[indice % PALETA_CATEGORICA.length];

/**
 * Barras horizontales apiladas: **litros de producto comercial por lote**, una
 * serie por producto.
 *
 * Responde la pregunta que los KPI no contestan — los totales dicen cuánta
 * mezcla se aplicó, no cuánto producto se gastó ni dónde —, y se actualiza
 * mientras se teclea, así que un error de dedo en una caneca se ve antes de
 * guardar.
 *
 * Horizontal y apilado, como el mockup: los nombres de lote son largos y en
 * vertical quedarían rotados, y apilar deja leer de un vistazo el total del
 * lote además del reparto entre productos.
 *
 * No extiende `EChartRender` porque no se monta solo: vive dentro de la
 * plantilla del campo de lotes, que ya tiene su propio `connect()`/`dispose()`.
 */
export class AplicacionProductoLoteChart {
  /** @param _contenedorId Id del `<div>` donde se dibuja (lo crea la plantilla). */
  constructor(private readonly _contenedorId: string) {}

  /** Inicializa el gráfico sobre su `<div>`. Si no existe, no hace nada: el
   *  campo de lotes puede estar oculto (método dron). */
  connect(): void {
    const el = document.getElementById(this._contenedorId);
    if (!el) return;
    this._chart = echarts.init(el);
    // Se redimensiona con su contenedor y no con la ventana: el contenedor
    // cambia de ancho al mostrarse la card, sin que la ventana se mueva.
    this._resizeObserver = new ResizeObserver(() => this._chart?.resize());
    this._resizeObserver.observe(el);
  }

  dispose(): void {
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._chart?.dispose();
    this._chart = null;
  }

  /**
   * Redibuja con los datos actuales.
   *
   * `notMerge: true`: al quitar un producto o un lote hay que **reemplazar** el
   * arreglo de series, no fusionarlo — si no, las series viejas sobrevivirían
   * al merge y el gráfico mostraría un producto que ya no está.
   */
  refresh(
    lotes: readonly LoteMedido[],
    productos: readonly ProductoDosis[],
    parametros: ParametrosMedicion,
  ): void {
    if (!this._chart) return;

    // Sin lotes o sin productos no hay nada que comparar: se limpia en vez de
    // dejar el dibujo anterior, que describiría datos que ya no existen.
    if (lotes.length === 0 || productos.length === 0) {
      this._chart.clear();
      return;
    }

    const series: echarts.BarSeriesOption[] = productos.map(
      (producto, indice) => ({
        name: producto.nombre,
        type: "bar",
        stack: "total",
        barMaxWidth: 16,
        itemStyle: {
          color: fx_colorProducto(indice),
          borderRadius: 2,
        },
        data: lotes.map((lote) =>
          Number(
            getLitrosProducto(
              lote,
              parametros,
              producto.dosisMlPorRecipiente,
            ).toFixed(2),
          ),
        ),
      }),
    );

    // Alto proporcional a la cantidad de lotes: con uno solo, un alto fijo deja
    // una barra perdida en el medio; con diez, las barras se aplastan.
    const el = document.getElementById(this._contenedorId);
    if (el) el.style.height = `${Math.max(110, 38 * lotes.length + 30)}px`;
    this._chart.resize();

    this._chart.setOption(
      {
        grid: { left: 8, right: 16, top: 8, bottom: 8, containLabel: true },
        // Sin leyenda propia: la arma el campo de lotes, porque la del gráfico da
        // el nombre del producto pero no **cuántos litros** suma en toda la
        // aplicación, que es el dato con el que se compra.
        legend: { show: false },
        tooltip: {
          trigger: "item",
          valueFormatter: (valor: unknown) => `${String(valor)} L`,
        },
        xAxis: {
          type: "value",
          axisLabel: { fontSize: 9, formatter: "{value} L" },
          splitLine: { lineStyle: { color: "rgba(211,209,199,.4)" } },
        },
        yAxis: {
          type: "category",
          data: lotes.map((lote) => lote.loteNombre),
          axisLabel: { fontSize: 11 },
          axisTick: { show: false },
        },
        series,
      },
      true,
    );
  }

  private _chart: echarts.ECharts | null = null;
  private _resizeObserver: ResizeObserver | null = null;
}
